// ── M13: Preview Mode Sample Data ────────────────────────────
// Provides realistic sample data when user is in sneak-peek preview,
// so they can explore the app's features before signing up.

import type { Task, Profile, Relationship, PointsEntry, FeedItem } from '@/types'
import type { ProfileStats } from '@/types/profile'
import type { TaskFilters } from '@/types/task'

// ── Preview Mode Flag ──

let _previewMode = false

export function setPreviewMode_M13_1(active: boolean) {
  _previewMode = active
}

export function isPreviewMode_M13_2(): boolean {
  return _previewMode
}

// ── Date Helper ──

function daysFromNow(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  d.setHours(12, 0, 0, 0)
  return d.toISOString()
}

// ── Preview Profiles ──

const PREVIEW_DOM_PROFILE: Profile = {
  id: 'preview_dom',
  email: 'alex@preview',
  role: 'dom',
  display_name: 'Alex',
  is_switch_paid: false,
  pronouns: 'they/them',
  bio: 'Your devoted guide.',
  calendar_public: false,
  created_at: daysFromNow(-30),
  updated_at: daysFromNow(-1),
}

const PREVIEW_GUEST_PROFILE: Profile = {
  id: 'preview_guest',
  email: 'guest@preview',
  role: 'sub',
  display_name: 'Guest',
  is_switch_paid: false,
  pronouns: null,
  bio: 'Preview mode — sign up to make it yours.',
  calendar_public: false,
  created_at: daysFromNow(-14),
  updated_at: daysFromNow(0),
}

// ── Preview Relationship ──

const PREVIEW_RELATIONSHIP: Relationship = {
  id: 'preview_rel_1',
  dom_id: 'preview_dom',
  sub_id: 'preview_guest',
  status: 'active',
  initiated_by: 'preview_dom',
  created_at: daysFromNow(-14),
}

// ── Preview Tasks ──

const PREVIEW_TASKS: Task[] = [
  {
    id: 'preview_task_1',
    relationship_id: 'preview_rel_1',
    title: 'Morning devotion journal',
    description: 'Write 3 things you appreciate about our dynamic today.',
    points: 10,
    spice_level: 1,
    reward_icon: null,
    due_date: daysFromNow(1),
    status: 'pending',
    proof_required: false,
    proof_url: null,
    frequency: 'daily',
    created_by: 'preview_dom',
    created_at: daysFromNow(-1),
    completed_at: null,
  },
  {
    id: 'preview_task_2',
    relationship_id: 'preview_rel_1',
    title: 'Choose your outfit for tonight',
    description: 'Pick something that makes you feel confident. Send a photo for approval.',
    points: 15,
    spice_level: 2,
    reward_icon: null,
    due_date: daysFromNow(0),
    status: 'pending',
    proof_required: true,
    proof_url: null,
    frequency: 'once',
    created_by: 'preview_dom',
    created_at: daysFromNow(-1),
    completed_at: null,
  },
  {
    id: 'preview_task_3',
    relationship_id: 'preview_rel_1',
    title: 'Practice kneeling for 10 minutes',
    description: 'Focus on your breathing and posture. Time yourself.',
    points: 20,
    spice_level: 3,
    reward_icon: null,
    due_date: daysFromNow(-1),
    status: 'completed',
    proof_required: true,
    proof_url: null,
    frequency: 'daily',
    created_by: 'preview_dom',
    created_at: daysFromNow(-3),
    completed_at: daysFromNow(-1),
  },
  {
    id: 'preview_task_4',
    relationship_id: 'preview_rel_1',
    title: "Write 3 things you're grateful for",
    description: 'Reflect on what your Dom has done for you this week.',
    points: 10,
    spice_level: 1,
    reward_icon: null,
    due_date: daysFromNow(-3),
    status: 'verified',
    proof_required: false,
    proof_url: null,
    frequency: 'weekly',
    created_by: 'preview_dom',
    created_at: daysFromNow(-5),
    completed_at: daysFromNow(-3),
  },
  {
    id: 'preview_task_5',
    relationship_id: 'preview_rel_1',
    title: 'Photograph your collar',
    description: 'Take a creative photo wearing your collar. Be artistic!',
    points: 25,
    spice_level: 2,
    reward_icon: null,
    due_date: daysFromNow(-4),
    status: 'verified',
    proof_required: true,
    proof_url: null,
    frequency: 'once',
    created_by: 'preview_dom',
    created_at: daysFromNow(-6),
    completed_at: daysFromNow(-4),
  },
  {
    id: 'preview_task_6',
    relationship_id: 'preview_rel_1',
    title: 'Edge for 15 minutes',
    description: 'Follow the guided routine. No finishing without permission.',
    points: 30,
    spice_level: 4,
    reward_icon: null,
    due_date: daysFromNow(2),
    status: 'pending',
    proof_required: false,
    proof_url: null,
    frequency: 'once',
    created_by: 'preview_dom',
    created_at: daysFromNow(0),
    completed_at: null,
  },
  {
    id: 'preview_task_7',
    relationship_id: 'preview_rel_1',
    title: 'Prepare a special dinner',
    description: 'Cook something your Dom has been craving.',
    points: 15,
    spice_level: 1,
    reward_icon: null,
    due_date: daysFromNow(-2),
    status: 'rejected',
    proof_required: true,
    proof_url: null,
    frequency: 'once',
    created_by: 'preview_dom',
    created_at: daysFromNow(-4),
    completed_at: daysFromNow(-2),
  },
]

// ── Preview Points ──

const PREVIEW_POINTS: PointsEntry[] = [
  {
    id: 'preview_pts_1',
    user_id: 'preview_guest',
    task_id: 'preview_task_4',
    points: 10,
    reason: "Task verified: Write 3 things you're grateful for",
    created_at: daysFromNow(-2),
  },
  {
    id: 'preview_pts_2',
    user_id: 'preview_guest',
    task_id: 'preview_task_5',
    points: 25,
    reason: 'Task verified: Photograph your collar',
    created_at: daysFromNow(-3),
  },
]

const PREVIEW_TOTAL_POINTS = 35

// ── Preview Feed ──

const PREVIEW_FEED: FeedItem[] = [
  {
    id: 'preview_feed_1',
    task: PREVIEW_TASKS[3],
    submittedBy: PREVIEW_GUEST_PROFILE,
    verifiedBy: PREVIEW_DOM_PROFILE,
    proofUrl: null,
    completedAt: daysFromNow(-3),
    relationshipId: 'preview_rel_1',
  },
  {
    id: 'preview_feed_2',
    task: PREVIEW_TASKS[4],
    submittedBy: PREVIEW_GUEST_PROFILE,
    verifiedBy: PREVIEW_DOM_PROFILE,
    proofUrl: null,
    completedAt: daysFromNow(-4),
    relationshipId: 'preview_rel_1',
  },
  {
    id: 'preview_feed_3',
    task: PREVIEW_TASKS[2],
    submittedBy: PREVIEW_GUEST_PROFILE,
    verifiedBy: null,
    proofUrl: null,
    completedAt: daysFromNow(-1),
    relationshipId: 'preview_rel_1',
  },
]

// ── Preview Profile Stats ──

const PREVIEW_STATS: ProfileStats = {
  totalPoints: 35,
  tasksCompleted: 3,
  tasksAssigned: 7,
  tasksVerified: 2,
  tasksRejected: 1,
  currentStreak: 3,
}

// ── Public API ──

export function getPreviewTasks_M13_3(filters?: TaskFilters): Task[] {
  if (filters?.status && filters.status !== 'all') {
    return PREVIEW_TASKS.filter((t) => t.status === filters.status)
  }
  return PREVIEW_TASKS
}

export function getPreviewTaskById_M13_4(taskId: string): Task | null {
  return PREVIEW_TASKS.find((t) => t.id === taskId) ?? null
}

export function getPreviewRelationships_M13_5(): Relationship[] {
  return [PREVIEW_RELATIONSHIP]
}

export function getPreviewFeed_M13_6(): FeedItem[] {
  return PREVIEW_FEED
}

export function getPreviewPoints_M13_7(): PointsEntry[] {
  return PREVIEW_POINTS
}

export function getPreviewTotalPoints_M13_8(): number {
  return PREVIEW_TOTAL_POINTS
}

export function getPreviewProfile_M13_9(userId: string): Profile | null {
  if (userId === 'preview_dom') return PREVIEW_DOM_PROFILE
  // Any other userId in preview mode gets the guest profile
  return PREVIEW_GUEST_PROFILE
}

export function getPreviewProfileStats_M13_10(): ProfileStats {
  return PREVIEW_STATS
}

export function getPreviewCalendarTasks_M13_11(): Task[] {
  return PREVIEW_TASKS.filter((t) => t.due_date)
}
